import {
  Loading,
  LoadingBlock,
  Table,
  TableColumnInstance,
} from '@orobix/orobix-fe-components'
import i18n from 'i18next'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'
import { useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'

import {
  Dataset,
  DATASET_STARTED,
  DatasetType,
  getDatasetStatusStr,
  getDatasetTypeStr,
} from '../../../types/dataset'
import { formatDateTime } from '../../../utils/date'
import { createSocket } from '../../../utils/webSocket'
import { useDatasets } from './useDatasets'

interface Columns extends Dataset {
  type?: DatasetType
}

const columns: TableColumnInstance<Columns, keyof Columns>[] = [
  {
    id: 'id',
    header: i18n.t('Id'),
    th: true,
    template: ({ id, name }) => <span title={name}>#{id}</span>,
  },
  {
    id: 'createdAt',
    header: i18n.t('Start process'),
    template: ({ createdAt }) => (createdAt ? formatDateTime(createdAt) : '-'),
  },
  {
    id: 'status',
    header: i18n.t('Status'),
    template: ({ status }) => (
      <>
        {getDatasetStatusStr(status)}{' '}
        {status === DATASET_STARTED && <Loading />}
      </>
    ),
  },
  {
    id: 'type',
    header: i18n.t('Type'),
    template: ({ type }) => (type ? getDatasetTypeStr(type) : '-'),
  },
]

export const DatasetsList = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { datasetId } = useParams<{ datasetId: string }>()
  const { data, isEmpty, hasNextPage, fetchNextPage } = useDatasets()

  // WebSocket connection for real-time status updates
  useEffect(() => {
    const websocket = createSocket('/data-ingestion/status')

    websocket.onmessage = (event: MessageEvent<string>) => {
      queryClient.invalidateQueries(['data-ingestions'])
    }

    return () => {
      websocket.close()
    }
  }, [queryClient])

  return (
    <InfiniteScroll
      loadMore={() => fetchNextPage()}
      hasMore={hasNextPage}
      loader={<LoadingBlock message={t('Loading')} key="loading" />}
    >
      <Table
        columns={columns}
        data={data as Columns[]}
        alternateRows
        stickyHeader
        isEmpty={isEmpty}
        emptyMessage={t(
          `There's nothing here. Try creating a new {{ itemName }}.`,
          { itemName: t('Dataset').toLowerCase() }
        )}
        selectedRow={data.findIndex((d) => d.id === Number(datasetId))}
      />
    </InfiniteScroll>
  )
}
